import type {
  BookingSlot,
  MealUnion,
  Meeting,
  SummarizedActivitiesFragment,
} from "@/graphql/generated-types";
import type { ICalendarItem } from "@/stories/dashboard/CalendarItem/CalendarItem.vue";
import { toUnix } from "@/stories/utils/dates";
import type { ListViewScope } from "@/types";

const fromActivity = (item: SummarizedActivitiesFragment): ICalendarItem => {
  return {
    id: item.id,
    title: item.title,
    startAt: toUnix(new Date(item.startDate)),
    endAt: toUnix(new Date(item.endDate)),
    image: item.image?.source || "",
    module: "activity",
    departments: (item.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
    isCancelled: item.cancelled || false,
    isRepeat: item.__typename === "RepeatingActivity",
  };
};

const fromMeeting = (item: Meeting): ICalendarItem => {
  return {
    id: item.id,
    title: item.title,
    startAt: toUnix(new Date(item.startDate)),
    endAt: toUnix(new Date(item.endDate)),
    image: item.coverImage?.source || "",
    module: "meeting",
    departments: (item.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
    isCancelled: item.cancelled || false,
    isRepeat: item.__typename === "RepeatingMeeting",
  };
};

const fromBooking = (item: BookingSlot): ICalendarItem => {
  return {
    id: item.id,
    title: item.description || item.resource.title,
    startAt: toUnix(new Date(item.start)),
    endAt: toUnix(new Date(item.end)),
    image: item.resource.coverImage?.source || "",
    module: "booking",
    departments: (item.resource.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
  };
};

const fromMeal = (item: MealUnion): ICalendarItem => {
  return {
    id: item.id,
    title: item.title,
    startAt: toUnix(new Date(item.startDate)),
    endAt: toUnix(new Date(item.endDate)),
    image: item.coverImage?.source || "",
    module: "meal",
    departments: (item.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
    isCancelled: item.cancelled || false,
  };
};

const getFilterBySelectedDate = (item: ICalendarItem): ListViewScope => {
  const now = toUnix(new Date());

  if (item.startAt < now || item.endAt < now) {
    return "show_previous";
  }

  return "show_upcoming";
};

const toActivityLink = (item: ICalendarItem): string => {
  const url = new URL("activities/main", window.location.origin);
  url.searchParams.append("id", item.id);
  url.searchParams.append("filter", getFilterBySelectedDate(item));
  return url.pathname + url.search;
};

const toMealLink = (item: ICalendarItem): string => {
  const url = new URL("meals/main", window.location.origin);
  url.searchParams.append("id", item.id);
  url.searchParams.append("filter", getFilterBySelectedDate(item));
  return url.pathname + url.search;
};

const toBookingLink = (): string => {
  return "/booking/bookable-bookings";
};

const toMeetingLink = (item: ICalendarItem) => {
  const url = new URL("meeting/meetings/main", window.location.origin);
  url.searchParams.append("id", item.id);
  url.searchParams.append("filter", getFilterBySelectedDate(item));
  return url.pathname + url.search;
};

export default {
  fromActivity,
  fromMeal,
  fromMeeting,
  fromBooking,
  toActivityLink,
  toMealLink,
  toBookingLink,
  toMeetingLink,
};
